import Controller from "@ember/controller";
import { task, all } from "ember-concurrency";
import { service } from "@ember/service";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import { query, saveRecord } from "@warp-drive/legacy/compat/builders";
import { waitForPromise } from "@ember/test-waiters";
import { SilentError } from "glesys-controlpanel/services/error-handler";
import destroyRecord from "glesys-controlpanel/utils/destroy-record";

export default class extends Controller {
  @service store;
  @service ajax;
  @service session;
  @service notification;
  @service router;

  @tracked dataCenter = "Falkenberg";
  @tracked host;
  @tracked domain;
  @tracked email = this.session.get("currentUser").id;
  @tracked username = this.session.get("currentUser").defaultServerUsername;
  @tracked password;
  @tracked sshKey;
  @tracked apiKey;

  get project() {
    return this.model.project;
  }

  get server() {
    return this.submit.last.value;
  }

  dataCenters = [{ value: "Falkenberg" }, { value: "Stockholm" }];

  get canSubmit() {
    return (
      (!this.enableSubdomain || this.host) &&
      this.domain &&
      this.email &&
      this.username &&
      (this.sshKey || this.password)
    );
  }

  get applianceConfig() {
    return {
      cpucores: 1,
      disksize: 20,
      memorysize: 1024,
      bandwidth: 100,
    };
  }

  onChange = ({ email }) => (this.email = email);

  @action
  onUserChange(username, password, sshkey) {
    this.username = username;
    this.password = password;
    this.sshKey = sshkey;
  }

  @action
  selectDataCenter(dataCenter) {
    this.dataCenter = dataCenter;
  }

  @action
  hostnameUpdated(host, domain) {
    this.host = host;
    this.domain = domain;
  }

  resetProperties() {
    this.password = "";
    this.sshKey = "";
    this.email = this.session.get("currentUser").id;
  }

  getAvailableIps() {
    return all(
      [4, 6].map((ipversion) => {
        return this.store
          .request(
            query("available-ip", {
              projectkey: this.project.id,
              platform: "KVM",
              datacenter: this.dataCenter,
              ipversion,
            }),
          )
          .then((response) => response.content);
      }),
    );
  }

  getRandomIp(availableIps, type) {
    let ips = type == "ipv4" ? availableIps[0] : availableIps[1];
    let random = Math.floor(Math.random() * ips.length);

    return ips.at(random);
  }

  createDnsRecord(ipv4, host) {
    return this.store.createRecord("dns-record", {
      type: "A",
      domain: this.domain,
      host: host,
      rdata: ipv4,
      domainname: this.domain.id,
    });
  }

  async fetchCloudConfig() {
    let response = await waitForPromise(
      fetch("https://raw.githubusercontent.com/glesys/one-click-installers/master/wordpress/cloud-config.yaml"),
    );

    if (!response.ok) {
      throw new SilentError("Could not fetch cloudconfig");
    }

    return response.text();
  }

  submit = task(async () => {
    let user = { username: this.username };
    this.sshKey && (user.sshkeys = [this.sshKey]);
    this.password && (user.password = this.password);

    let hostname = [this.host, this.domain.id].filter((x) => x).join(".");

    let cloudconfig = await this.fetchCloudConfig();

    let data = {
      project: this.project,
      datacenter: this.dataCenter,
      platform: "KVM",
      hostname: this.host ? hostname : `www.${hostname}`,
      templatename: "ubuntu-24-04",
      backupschedules: [],
      cloudconfig,
      cloudconfigparams: {
        hostname: hostname,
        email: this.email,
      },
      users: [user],
      ...this.applianceConfig,
    };

    // get available IPs
    let availableIps = await this.getAvailableIps();

    // get random IPs from available
    let ipv4 = await this.getRandomIp(availableIps, "ipv4");
    data.ip = ipv4.address;

    let ipv6 = await this.getRandomIp(availableIps, "ipv6");
    data.ipv6 = ipv6.address;

    let host = this.host || "www";

    let records = [await this.createDnsRecord(data.ip, host)];
    if (!this.host) {
      data.cloudconfigparams.hostnames = [{ name: `www.${hostname}` }];
      records.push(await this.createDnsRecord(data.ip, "@"));
    }

    let server = this.store.createRecord("server", data);

    try {
      for (const record of records) {
        await this.store.request(saveRecord(record));
      }
      await this.store.request(saveRecord(server));

      // set PTR on ip addresses
      let reservedIps = await this.project.hasMany("reservedIps").reload();
      reservedIps.forEach((ip) => {
        if (ip.address == data.ip || ip.address == data.ipv6) {
          ip.set("ptr", this.host ? `${hostname}.` : `www.${hostname}.`);
          this.store.request(saveRecord(ip)).catch(() => ip.rollbackAttributes());
        }
      });

      this.router.replaceWith("servers.appliance.creating", server.id, "wordpress");
    } catch (error) {
      for (const record of records) {
        await destroyRecord(record);
      }
      await server.deleteRecord();
      this.notification.add("error", error.errors[0].title, error.errors[0].detail);
    }
  });
}
